import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { Button } from 'govuk-react-jsx';
import { useSelector } from 'react-redux';
import {
  getCurrentLanguage,
  getCopyProviderCY,
  getCopyProviderEN,
  getDashboardLink,
} from '../../features/app/appSlice';
import { getSubmittedApplicationId } from '../../features/dashboard/dashboardSlice';
import { getApplicationClaim, getWordingTemplates } from '../../firebase';
import useGaEndEvent from '../../hooks/useGaEndEvent';
import useTitle from '../../hooks/useTitle';
import { useSetReduxState } from '../../hooks/useResetReduxState';

const ApplicationSubmitted = () => {
  const [setResetReduxState] = useSetReduxState();
  const dashboardLink = useSelector(getDashboardLink);
  const currentLanguage = useSelector(getCurrentLanguage);
  const copyProviderCY = useSelector(getCopyProviderCY);
  const copyProviderEN = useSelector(getCopyProviderEN);
  const copyProvider =
    currentLanguage === 'en' ? copyProviderEN : copyProviderCY;
  const [dynamicWording, setDynamicWording] = useState({
    message: null,
    is_dynamic: false,
  });
  const [isLoading, setIsLoading] = useState(true);

  useTitle(
    'landlordApp.ApplicationSubmitted.s27',
    'You have submitted your application - OZEV Electric Vehicle Chargepoint Grant - GOV.UK'
  );

  const [claimData, setClaimData] = useState({});

  const applicationId = useSelector(getSubmittedApplicationId);

  useGaEndEvent('LanlordSubmitClaim', 'lr_claim_end');

  const handleButton = () => {
    setResetReduxState('applicationJourney');
    window.location.assign('/dashboardHub/dashboard');
  };

  useEffect(async () => {
    // .................
    const fetchWordingTemplates = async () => {
      try {
        // Fetch claim data
        const claimResponse = await getApplicationClaim({
          id: applicationId,
        });
        if (dashboardLink) {
          const { data } = claimResponse;

          const claimDealineDate = new Date(
            // eslint-disable-next-line no-underscore-dangle
            data.claim_submission_deadline_date._seconds * 1000
          );
          const claimDeadlineDateFormatted = format(
            claimDealineDate,
            'dd MMMM yyyy'
          );

          setClaimData({
            ...data,
            claimDeadlineDateFormatted,
          });
        }
        setIsLoading(true);

        // Fetch wording templates
        const wordingResponse = await getWordingTemplates({
          grantType: claimResponse.data.grant_type,
        });

        if (wordingResponse.data?.success && wordingResponse.data?.data) {
          setDynamicWording({
            message: wordingResponse.data.data,
            is_dynamic: wordingResponse.data.data.is_dynamic,
          });
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        if (error.code === 'functions/permission-denied') {
          window.location.assign(dashboardLink);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchWordingTemplates();
  }, [applicationId, dashboardLink]);
  const getMessage = () => {
    // Before activation → Show hardcoded
    if (!dynamicWording.is_dynamic) {
      return copyProvider.getCopy(
        'landlordApp.ApplicationSubmitted.s21',
        'You will have 7 working days to challenge it. If no action is taken, we will accept the claim and approve for payment.'
      );
    }

    // After activation:

    const message =
      currentLanguage === 'en'
        ? dynamicWording.message.workingDaysChallenge7_LandLordmessage_en
        : dynamicWording.message.workingDaysChallenge7_LandLordmessage_cy;

    if (message === '') {
      return '';
    }

    // If null or undefined → Show hardcoded
    if (message === null || message === undefined) {
      return copyProvider.getCopy(
        'landlordApp.ApplicationSubmitted.s21',
        'You will have 7 working days to challenge it. If no action is taken, we will accept the claim and approve for payment.'
      );
    }

    // If has value → Show dynamic text
    return message;
  };
  if (isLoading) {
    return (
      <div className="govuk-grid-row">
        <p className="govuk-body">Loading...</p>
      </div>
    );
  }
  const pageModel = (
    <div>
      <div className="govuk-panel govuk-panel--confirmation">
        <h1 className="govuk-panel__title">
          {copyProvider.getCopy(
            'landlordApp.ApplicationSubmitted.s1',
            'Application submitted'
          )}
        </h1>
        <div className="govuk-panel__body">
          {copyProvider.getCopy(
            'landlordApp.ApplicationSubmitted.s2',
            'Your reference number'
          )}
          <br />
          <strong>{claimData.claim_id}</strong>
        </div>
      </div>
      <p className="govuk-body">
        {copyProvider.getCopy(
          'landlordApp.ApplicationSubmitted.s3',
          'We have emailed your installer to let them know you have finished your part of the application.'
        )}
      </p>

      <div>
        <h2 className="govuk-heading-m">
          {copyProvider.getCopy(
            'landlordApp.ApplicationSubmitted.s5',
            'What happens now'
          )}
        </h2>
        <p className="govuk-body">
          {copyProvider.getCopy(
            'landlordApp.ApplicationSubmitted.s7',
            'You can now contact your installer to get the chargepoints installed.'
          )}
        </p>
        <h2 className="govuk-heading-m">
          {copyProvider.getCopy(
            'landlordApp.ApplicationSubmitted.s9',
            'What happens after the installation'
          )}
        </h2>
        <h3 className="govuk-heading-s">
          {copyProvider.getCopy(
            'landlordApp.ApplicationSubmitted.s13',
            'Installer will submit their part of the claim'
          )}
        </h3>
        <p className="govuk-body">
          {copyProvider.getCopy(
            'landlordApp.ApplicationSubmitted.s14',
            'When the installation is finished, your installer will have to submit the evidence. This includes photos, chargepoint serial numbers and the invoice they have given to you.'
          )}
        </p>
        <h3 className="govuk-heading-s">
          {copyProvider.getCopy(
            'landlordApp.ApplicationSubmitted.s19',
            'You can review the claim submitted by your installer'
          )}
        </h3>
        <p className="govuk-body">
          {copyProvider.getCopy(
            'landlordApp.ApplicationSubmitted.s20',
            'When your installer submits the claim for this application, you will receive an email to review it.'
          )}
        </p>
        <p className="govuk-body">{getMessage()}</p>
      </div>

      <Button
        className="govuk-button govuk-!-margin-top-4"
        onClick={handleButton}
      >
        {copyProvider.getCopy(
          'landlordApp.ApplicationSubmitted.s22',
          'Manage your applications'
        )}
      </Button>

      <h3 className="govuk-heading-s govuk-!-margin-top-4">
        {copyProvider.getCopy(
          'landlordApp.ApplicationSubmitted.s23',
          'Help to make this service better'
        )}
      </h3>
      <p className="govuk-body">
        <a
          href="https://www.smartsurvey.co.uk/s/EV-grants/"
          target="_blank"
          rel="noopener noreferrer"
          className="govuk-link"
        >
          {copyProvider.getCopy(
            'landlordApp.ApplicationSubmitted.s24',
            'What did you think of this service?'
          )}
        </a>{' '}
        {copyProvider.getCopy(
          'landlordApp.ApplicationSubmitted.s26',
          ' (takes 30 seconds)'
        )}
      </p>
    </div>
  );
  return pageModel;
};

export default ApplicationSubmitted;
